import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { MonitorDown, Share, Smartphone } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  detectInstallPlatform,
  detectStandalone,
  getDeferredInstallPrompt,
  getInstallPhase,
  installHintKey,
  markInstalled,
  readInstallEnvironment,
  setDeferredInstallPrompt,
  subscribeToInstallState,
  type BeforeInstallPromptEvent,
  type InstallPlatform,
} from './install-flow';

/**
 * Persistent "install the app" control.
 *
 * The previous version of this was a dismissible banner that appeared after
 * `beforeinstallprompt` fired. That had two problems: it only existed on
 * Chromium, because only Chromium fires the event, and it disappeared for good
 * the first time somebody dismissed it — leaving Safari and Firefox users, and
 * anyone who cleared the banner by reflex, with no way to install at all.
 *
 * This is a permanent icon button in the header instead. It sits to the left of
 * the language picker, and what a click does depends on what the browser can
 * do:
 *
 *   - Chromium has parked a `beforeinstallprompt` event → the browser's own
 *     install sheet opens;
 *   - anything else (Safari and Firefox never fire it; on Firefox for Android
 *     the browser offers its own menu entry) → a short dialog with the steps
 *     that platform actually needs.
 *
 * It renders nothing once the app is running as an installed app, which is the
 * only time showing it would be wrong.
 */

/** The icon that heads the instructions for a platform. */
const PLATFORM_ICON: Record<InstallPlatform, typeof Share> = {
  ios: Share,
  android: Smartphone,
  desktop: MonitorDown,
  other: MonitorDown,
};

/** A snapshot that is the same on the server as on the first client render. */
const serverSnapshot = (): ReturnType<typeof getInstallPhase> => 'manual';

export function InstallButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  const phase = useSyncExternalStore(subscribeToInstallState, getInstallPhase, serverSnapshot);

  const [platform, setPlatform] = useState<InstallPlatform>('other');
  const [hintsOpen, setHintsOpen] = useState(false);
  const [prompting, setPrompting] = useState(false);

  useEffect(() => {
    const environment = readInstallEnvironment();

    // Launched from a home screen or an installed window: there is nothing to
    // offer, and the store drops the button entirely.
    if (detectStandalone(environment)) {
      markInstalled();
      return undefined;
    }

    setPlatform(detectInstallPlatform(environment));

    const onBeforeInstallPrompt = (event: Event) => {
      // Suppressing the browser's own mini-infobar keeps the offer in one
      // place; without it Chromium may show both.
      event.preventDefault();
      setDeferredInstallPrompt(event as BeforeInstallPromptEvent);
    };

    const onInstalled = () => markInstalled();

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    const deferred = getDeferredInstallPrompt();

    if (!deferred) {
      setHintsOpen(true);
      return;
    }

    // A deferred event can only be replayed once; parking it again would put a
    // spent event back in the store, and the next click would find it refused.
    setDeferredInstallPrompt(null);
    setPrompting(true);

    try {
      await deferred.prompt();
      // The outcome is deliberately not acted on. `userChoice` resolves when
      // the sheet closes, not when the app lands on the home screen, and it
      // reports a dismissal for an install the user completed through the
      // browser's own UI. The `appinstalled` event is the honest signal, and
      // it is already wired above.
      await deferred.userChoice;
    } catch {
      // A prompt that fails to open (already installed elsewhere, user gesture
      // not recognised) is not an error worth surfacing: the dialog below lists
      // the manual steps, which accomplish the same thing.
      setHintsOpen(true);
    } finally {
      setPrompting(false);
    }
  };

  // Nothing to offer to somebody already running the installed app.
  if (phase === 'installed') {
    return null;
  }

  const HintIcon = PLATFORM_ICON[platform];
  const label = t('pwa.installTitle');

  return (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        className={className}
        onClick={() => void install()}
        loading={prompting}
        aria-label={label}
        title={label}
      >
        <MonitorDown />
      </Button>

      <Dialog open={hintsOpen} onOpenChange={setHintsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pwa.installTitle')}</DialogTitle>
            <DialogDescription>{t('pwa.installBody')}</DialogDescription>
          </DialogHeader>

          <div className="bg-muted/50 flex items-start gap-3 rounded-lg border p-3">
            <div className="bg-primary/12 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
              <HintIcon className="size-4" aria-hidden />
            </div>
            <p className="text-sm">{t(installHintKey(platform))}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHintsOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
